import { FluentProvider, Link, MessageBar, MessageBarBody, webLightTheme } from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { getErrorMessage } from "Common/ErrorHandlingUtils";
import { userContext } from "UserContext";
import * as React from "react";
import ConnectImage from "../../../../images/HdeConnectCosmosDB.svg";
import { AuthType } from "../../../AuthType";
import { HttpStatusCodes } from "../../../Common/Constants";
import { fetchEncryptedToken, isAccountRestrictedForConnectionStringLogin } from "../../../Common/PortalBackendClient";
import { parseConnectionString } from "../Helpers/ConnectionStringParser";
import { isResourceTokenConnectionString } from "../Helpers/ResourceTokenUtils";
import { isDirectConnectionStringLoginApi } from "../HostedUtils";
export const ConnectExplorer = ({ setEncryptedToken, login, setAuthType, connectionString, setConnectionString, setAccountMetadata, }) => {
    const [isFormVisible, { setTrue: showForm }] = useBoolean(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [isBlockedByFirewall, setIsBlockedByFirewall] = React.useState(false);
    const [isConnecting, setIsConnecting] = React.useState(false);
    const enableConnectionStringLogin = !userContext.features.disableConnectionStringLogin;
    return (React.createElement("div", { id: "connectExplorer", className: "connectExplorerContainer", style: { display: "flex" } },
        React.createElement("div", { className: "connectExplorerFormContainer" },
            React.createElement("div", { className: "connectExplorer" },
                React.createElement("p", { className: "connectExplorerContent" },
                    React.createElement("img", { src: ConnectImage, alt: "Azure Cosmos DB" })),
                React.createElement("p", { className: "welcomeText" }, "Welcome to Azure Cosmos DB"),
                isFormVisible && enableConnectionStringLogin ? (React.createElement("form", { id: "connectWithConnectionString", "aria-busy": isConnecting, onSubmit: async (event) => {
                        event.preventDefault();
                        if (isConnecting) {
                            return;
                        }
                        setErrorMessage("");
                        setIsBlockedByFirewall(false);
                        setIsConnecting(true);
                        try {
                            if (await isAccountRestrictedForConnectionStringLogin(connectionString)) {
                                setErrorMessage("This account has been blocked from connection-string login. Please go to cosmos.azure.com/aad for AAD based login.");
                                setIsConnecting(false);
                                return;
                            }
                        }
                        catch (error) {
                            setErrorMessage(getErrorMessage(error));
                            setIsConnecting(false);
                            return;
                        }
                        try {
                            if (isResourceTokenConnectionString(connectionString)) {
                                setAuthType(AuthType.ResourceToken);
                                return;
                            }
                            const metadata = parseConnectionString(connectionString);
                            if (!metadata) {
                                setErrorMessage("We couldn't recognize this connection string. Verify that it is a valid Azure Cosmos DB connection string and try again.");
                                return;
                            }
                            if (isDirectConnectionStringLoginApi(metadata.apiKind)) {
                                setAccountMetadata(metadata);
                                setAuthType(AuthType.ConnectionString);
                                return;
                            }
                            // Mongo and Cassandra go through the Portal Backend
                            const encryptedToken = await fetchEncryptedToken(connectionString);
                            setEncryptedToken(encryptedToken);
                            setAuthType(AuthType.ConnectionString);
                        }
                        catch (error) {
                            const errorDetails = await error.text();
                            setErrorMessage(errorDetails
                                ? `Couldn't authenticate with Cosmos DB: ${errorDetails}`
                                : "Failed to connect to the account. Please check the connection string and try again.");
                            // A Forbidden usually means the account firewall dropped the request. The connection
                            // string is exchanged by the Portal Backend rather than the browser, so the account has
                            // to allowlist those services.
                            setIsBlockedByFirewall(error.status === HttpStatusCodes.Forbidden);
                        }
                        finally {
                            setIsConnecting(false);
                        }
                    } },
                    React.createElement("p", { className: "connectExplorerContent connectStringText" }, "Connect to your account with connection string"),
                    React.createElement("p", { className: "connectExplorerContent" },
                        React.createElement("input", { className: "inputToken", type: "text", required: true, placeholder: "Please enter a connection string", value: connectionString, onChange: (event) => {
                                setConnectionString(event.target.value);
                            } })),
                    errorMessage.length > 0 && (React.createElement(FluentProvider, { theme: webLightTheme, className: "connectErrorMessageBar" },
                        React.createElement(MessageBar, { intent: "error", layout: "multiline" },
                            React.createElement(MessageBarBody, null,
                                React.createElement("span", { className: "errorDetails" }, errorMessage),
                                isBlockedByFirewall && (React.createElement(Link, { className: "errorHelpLink", href: "https://learn.microsoft.com/azure/cosmos-db/how-to-configure-firewall#allow-requests-from-the-azure-portal", target: "_blank", rel: "noreferrer" }, "Allow access from Azure Portal")))))),
                    React.createElement("p", { className: "connectExplorerContent" },
                        React.createElement("input", { className: "filterbtnstyle", type: "submit", value: isConnecting ? "Connecting..." : "Connect", disabled: isConnecting })),
                    React.createElement("p", { className: "switchConnectTypeText", onClick: login }, "Sign In with Azure Account"))) : (React.createElement("div", { id: "connectWithAad" },
                    React.createElement("input", { className: "filterbtnstyle", type: "button", value: "Sign In", onClick: login }),
                    enableConnectionStringLogin && (React.createElement("p", { className: "switchConnectTypeText", "data-test": "Link:SwitchConnectionType", onClick: showForm }, "Connect to your account with connection string"))))))));
};
